import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { FormReviewPanel, FormReviewBadge } from "@/components/FormReviewPanel";
import GuardianDetailsPanel from "@/components/GuardianDetailsPanel";
import SubmittedFormViewer from "@/components/SubmittedFormViewer";
import LetterComposer from "@/components/LetterComposer";
import { noteDisplayFields } from "@/lib/noteFields";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, Mail, Phone, MapPin, AlertTriangle, Tag, Shield, Calendar,
  Receipt, FolderOpen, FileText, Mic, Clock, Plus, X, ChevronRight,
  ClipboardCheck, User, Heart, Pencil, Archive, RotateCcw, Bell,
  MessageSquare, Upload, FileCheck, CheckCircle2, Lock, Eye, Search,
  Download, Send, Trash2, ShieldCheck
} from "lucide-react";
import PrivacyDataRights from "@/components/patient/PrivacyDataRights";
import TreatmentNoteEditor from "@/components/TreatmentNoteEditor";
import { BodyChartPreview, BodyChartLightbox, getTemplateImage } from "@/components/BodyChartEditor";
import { generateNotePdf, generateFullHistoryPdf } from "@/lib/pdfSystem";
import { CopyButton } from "@/components/ui/copy-button";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };



function timeAgo(iso) {
  if (!iso) return "";
  const d = Date.now() - new Date(iso).getTime();
  const m = Math.floor(d / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const NAV_SECTIONS = [
  { key: "details", icon: User, label: "Details" },
  { key: "notes", icon: FileText, label: "Treatment notes" },
  { key: "appointments", icon: Calendar, label: "Appointments" },
  { key: "invoices", icon: Receipt, label: "Invoices" },
  { key: "account", icon: Receipt, label: "Account" },
  { key: "forms", icon: FileCheck, label: "Forms" },
  { key: "letters", icon: Mail, label: "Letters" },
  { key: "files", icon: FolderOpen, label: "Files" },
  { key: "consent", icon: Shield, label: "Consent" },
  { key: "timeline", icon: Clock, label: "Timeline" },
  { key: "privacy", icon: ShieldCheck, label: "Privacy & data rights" },
];

// Note: CONSENT_TYPES is now loaded dynamically from the API in PatientDetailPage

const ALERT_SEVERITY = { high: "bg-red-50 border-red-200 text-red-700", medium: "bg-amber-50 border-amber-200 text-amber-700", low: "bg-blue-50 border-blue-200 text-blue-700" };
const TIMELINE_ICONS = { patient_created: User, note_created: FileText, appointment_booked: Calendar, invoice_created: Receipt, file_uploaded: Upload, recall_created: Bell, consent_updated: Shield, consent_reviewed: Shield };

// Email Note Modal
function EmailNoteModal({ note, patient, clinic, user, onClose }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const to = patient?.email;

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const { data } = await api.post(`/clinical/notes/${note?.id}/email`, { message: message || null });
      if (data.email_status === "sent" || data.email_status === "mock") {
        setSent(data);
      } else {
        setError(data.message || "Email could not be sent. Please try again or download the PDF and send it manually.");
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Email could not be sent. Please try again or download the PDF and send it manually.");
    }
    setSending(false);
  };

  if (sent) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" data-testid="email-note-sent">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
        <CheckCircle2 className="w-10 h-10 text-[#205B5A] mx-auto mb-3" />
        <h2 className="text-lg font-medium text-[#1A1B1A] mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Treatment note email sent</h2>
        <p className="text-xs text-[#757675] mb-4">{sent.message || `Sent to ${to}`}</p>
        <button onClick={onClose} className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544]" data-testid="email-note-done">Done</button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" data-testid="email-note-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-[#1A1B1A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Email treatment note</h2>
          <button onClick={onClose} className="p-1 text-[#757675] hover:text-[#1A1B1A]"><X className="w-4 h-4" /></button>
        </div>

        {!to ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 mb-3" data-testid="email-note-no-address">
            <p className="text-xs text-amber-700">This patient does not have an email address saved.</p>
          </div>
        ) : (
          <div className="bg-[#FAF9F6] rounded-xl px-3 py-2.5 mb-3 space-y-1" data-testid="email-note-summary">
            <p className="text-[11px] text-[#4A4B4A]">To <span className="text-[#1A1B1A]">{to}</span></p>
            <p className="text-[11px] text-[#4A4B4A]">Record <span className="text-[#1A1B1A]">{note?.appointment_type || "Treatment note"}</span></p>
            <p className="text-[11px] text-[#4A4B4A]">Date <span className="text-[#1A1B1A]">{String(note?.date || note?.created_at || "").slice(0, 10)}</span></p>
            <p className="text-[11px] text-[#4A4B4A]">Practitioner <span className="text-[#1A1B1A]">{note?.practitioner_name || user?.full_name || user?.name || "—"}</span></p>
            <p className="text-[10px] text-[#757675] pt-1">
              The patient receives a secure link to view, print or save this record. No clinical detail is included in the email itself. Link expires in 30 days.
            </p>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-[#757675] mb-1">Short note to the patient (optional)</label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} data-testid="email-message"
            placeholder="e.g. Here is the record from your visit today."
            className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none leading-relaxed" />
        </div>

        {error && <p className="text-xs text-red-600 mt-2" data-testid="email-note-error">{error}</p>}

        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#757675] hover:text-[#1A1B1A]">Cancel</button>
          <button onClick={handleSend} disabled={sending || !to} data-testid="send-note-email-btn"
            className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544] disabled:opacity-40 flex items-center gap-1.5">
            <Send className="w-3.5 h-3.5" /> {sending ? "Sending..." : "Send secure link"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PatientDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user, clinic } = useAuth();
  const initialTab = searchParams.get("tab") || "details";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialTab);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [alertForm, setAlertForm] = useState({ alert_type: "", description: "", severity: "medium" });
  const [showAddFlag, setShowAddFlag] = useState(false);
  const [flagLabel, setFlagLabel] = useState("");
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteForm, setNoteForm] = useState(null);
  const [noteFilter, setNoteFilter] = useState("");
  const [emailModal, setEmailModal] = useState(null); // {note, patient}
  const [viewFormSubmission, setViewFormSubmission] = useState(null);
  const [fullFormSubmission, setFullFormSubmission] = useState(null);
  const [submissionPdfError, setSubmissionPdfError] = useState("");
  const [showComposer, setShowComposer] = useState(false);
  const [viewLetter, setViewLetter] = useState(null);
  const [editLetter, setEditLetter] = useState(null);
  const [deletingLetter, setDeletingLetter] = useState(null);

  const downloadLetterPdf = async (letter) => {
    try {
      const res = await api.get(`/letters/${letter.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(letter.subject || "Letter").replace(/[^\w-]+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setSubmissionPdfError("PDF could not be generated. Please try again or contact support.");
    }
  };

  const downloadSubmissionPdf = async (sub) => {
    setSubmissionPdfError("");
    try {
      const res = await api.get(`/forms/submissions/${sub.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const name = (res.headers?.["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || "form.pdf";
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setSubmissionPdfError("PDF could not be generated. Please try again or contact support.");
    }
  };
  const [viewFormLoading, setViewFormLoading] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [lightboxChart, setLightboxChart] = useState(null);
  const [consentTypes, setConsentTypes] = useState([]);
  const [patientConsents, setPatientConsents] = useState({ consents: [], pending_count: 0 });
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get(`/patients/${id}`).then(r => { setData(r.data); setEditForm(r.data.patient || {}); }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);
  
  const loadConsents = useCallback(() => {
    api.get(`/consent-types/patient/${id}`).then(r => setPatientConsents(r.data)).catch(() => {});
  }, [id]);
  
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/consent-types").then(r => setConsentTypes(r.data.consent_types)).catch(() => {}); }, []);
  useEffect(() => { loadConsents(); }, [loadConsents]);

  const saveDetails = async () => {
    await api.put(`/patients/${id}`, editForm);
    setEditing(false);
    load();
  };

  const addAlert = async (e) => { e.preventDefault(); await api.post("/clinical/alerts", { patient_id: id, ...alertForm }); setShowAddAlert(false); setAlertForm({ alert_type: "", description: "", severity: "medium" }); load(); };
  const removeAlert = async (aid) => { await api.delete(`/clinical/alerts/${aid}`); load(); };
  const addFlag = async () => { if (!flagLabel) return; await api.post("/clinical/flags", { patient_id: id, label: flagLabel }); setShowAddFlag(false); setFlagLabel(""); load(); };
  const removeFlag = async (fid) => { await api.delete(`/clinical/flags/${fid}`); load(); };

  const saveNote = async (e) => {
    e.preventDefault();
    if (noteForm?.id) {
      await api.put(`/clinical/notes/${noteForm.id}`, { ...noteForm, patient_id: id, status: "draft" });
    } else {
      await api.post("/clinical/notes", { patient_id: id, ...noteForm, status: "draft" });
    }
    setShowNoteForm(false); setNoteForm(null); load();
  };
  const updateConsent = async (consentTypeId, status) => { 
    await api.put(`/consent-types/patient/${id}/${consentTypeId}`, { status }); 
    loadConsents();
    load();
  };
  const archivePatient = async () => { await api.post(`/patients/${id}/archive`); navigate("/app/patients"); };
  const openFormSubmission = async (submissionId) => {
    setViewFormLoading(true);
    try {
      const { data } = await api.get(`/forms/submissions/${submissionId}`);
      setViewFormSubmission(data);
    } catch {}
    setViewFormLoading(false);
  };

  if (loading) return <div className="p-6 md:p-8"><div className="animate-pulse h-8 bg-[#1A1B1A]/[0.04] rounded-xl w-48" /></div>;
  if (!data) return <div className="p-6 md:p-8 text-sm text-[#757675]">Patient not found</div>;

  const p = data.patient;
  const inp = (field, ph, type = "text") => (
    <input value={editForm[field] || ""} onChange={e => setEditForm({...editForm, [field]: e.target.value})} type={type} placeholder={ph}
      className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] bg-white" />
  );

  return (
    <div className="flex h-full" data-testid="patient-detail-page">
      {/* Left sidebar nav */}
      <div className="w-[200px] border-r border-[#1A1B1A]/[0.06] bg-white flex-shrink-0 overflow-y-auto hidden lg:block">
        <div className="p-4">
          <Link to="/app/patients" className="text-[11px] text-[#757675] hover:text-[#1A1B1A] flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3" /> Patients</Link>
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-full bg-[#205B5A]/10 flex items-center justify-center"><span className="text-[10px] font-semibold text-[#205B5A]">{p.first_name?.[0]}{p.last_name?.[0]}</span></div>
            <div className="min-w-0"><p className="text-sm font-medium text-[#1A1B1A] truncate">{p.first_name} {p.last_name}</p><p className="text-[10px] text-[#757675]">{p.date_of_birth || "No DOB"}</p></div>
          </div>
          {/* Alerts badge */}
          {data.alerts?.length > 0 && <div className="mb-3 px-2 py-1.5 rounded-lg bg-red-50 border border-red-200"><p className="text-[10px] font-semibold text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {data.alerts.length} alert{data.alerts.length > 1 ? "s" : ""}</p></div>}
          {/* Flags */}
          {data.flags?.length > 0 && <div className="flex flex-wrap gap-1 mb-3">{data.flags.map(f => <span key={f.id} className="text-[9px] font-medium bg-[#205B5A]/10 text-[#205B5A] px-2 py-0.5 rounded-full">{f.label}</span>)}</div>}
        </div>
        <nav className="px-2 pb-4 space-y-0.5">
          {NAV_SECTIONS.map(s => (
            <button key={s.key} onClick={() => setTab(s.key)} data-testid={`patient-nav-${s.key}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${tab === s.key ? "bg-[#205B5A]/10 text-[#205B5A]" : "text-[#4A4B4A] hover:bg-[#FAF9F6]"}`}>
              <s.icon className="w-3.5 h-3.5" strokeWidth={1.5} />{s.label}
              {s.key === "notes" && data.notes?.length > 0 && <span className="ml-auto text-[9px] bg-[#1A1B1A]/[0.06] px-1.5 rounded-full">{data.notes.length}</span>}
              {s.key === "appointments" && data.appointments?.length > 0 && <span className="ml-auto text-[9px] bg-[#1A1B1A]/[0.06] px-1.5 rounded-full">{data.appointments.length}</span>}
              {s.key === "forms" && data.form_submissions?.length > 0 && <span className="ml-auto text-[9px] bg-[#1A1B1A]/[0.06] px-1.5 rounded-full">{data.form_submissions.length}</span>}
              {s.key === "files" && data.files?.length > 0 && <span className="ml-auto text-[9px] bg-[#1A1B1A]/[0.06] px-1.5 rounded-full">{data.files.length}</span>}
              {s.key === "letters" && data.letters?.length > 0 && <span className="ml-auto text-[9px] bg-[#1A1B1A]/[0.06] px-1.5 rounded-full">{data.letters.length}</span>}
              {s.key === "consent" && patientConsents.pending_count > 0 && <span className="ml-auto text-[9px] bg-red-500 text-white px-1.5 rounded-full">{patientConsents.pending_count}</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto">
        {/* Mobile header */}
        <div className="lg:hidden p-4 border-b border-[#1A1B1A]/[0.06] bg-white">
          <Link to="/app/patients" className="text-[11px] text-[#757675] flex items-center gap-1 mb-2"><ArrowLeft className="w-3 h-3" /> Patients</Link>
          <h1 className="text-xl font-medium text-[#1A1B1A]" style={H}>{p.first_name} {p.last_name}</h1>
          <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1">{NAV_SECTIONS.map(s => (
            <button key={s.key} onClick={() => setTab(s.key)} data-testid={`patient-nav-mobile-${s.key}`} className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${tab === s.key ? "bg-[#205B5A] text-white" : "bg-[#FAF9F6] text-[#757675]"}`}>{s.label}</button>
          ))}</div>
        </div>

        <div className="p-5 md:p-6 max-w-4xl">
          {/* ── DETAILS ── */}
          {tab === "details" && (
            <div className="space-y-5">
              {/* Medical Alerts */}
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Medical alerts</h3>
                  <button onClick={() => setShowAddAlert(true)} data-testid="add-alert-btn" className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544] flex items-center gap-0.5"><Plus className="w-3 h-3" /> Add</button>
                </div>
                {data.alerts?.length === 0 ? <p className="text-xs text-[#757675]">No medical alerts</p> : (
                  <div className="space-y-2">{data.alerts.map(a => (
                    <div key={a.id} className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${ALERT_SEVERITY[a.severity] || ALERT_SEVERITY.medium}`}>
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <div className="flex-1"><p className="text-xs font-semibold">{a.alert_type}</p><p className="text-[11px]">{a.description}</p></div>
                      <button onClick={() => removeAlert(a.id)} className="text-current opacity-40 hover:opacity-70"><X className="w-3 h-3" /></button>
                    </div>
                  ))}</div>
                )}
              </div>

              {/* Flags */}
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Patient flags</h3>
                  <button onClick={() => setShowAddFlag(true)} data-testid="add-flag-btn" className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544] flex items-center gap-0.5"><Plus className="w-3 h-3" /> Add</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {data.flags?.map(f => (
                    <span key={f.id} className="text-[11px] font-medium bg-[#205B5A]/10 text-[#205B5A] px-2.5 py-1 rounded-full flex items-center gap-1">
                      {f.label} <button onClick={() => removeFlag(f.id)} className="opacity-50 hover:opacity-100"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  ))}
                  {(!data.flags || data.flags.length === 0) && <p className="text-xs text-[#757675]">No flags</p>}
                </div>
              </div>

              {/* Patient Details */}
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider">Patient details</h3>
                  {!editing ? <button onClick={() => setEditing(true)} data-testid="edit-details-btn" className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544] flex items-center gap-0.5"><Pencil className="w-3 h-3" /> Edit</button>
                    : <div className="flex gap-2"><button onClick={() => setEditing(false)} className="text-[10px] text-[#757675]">Cancel</button><button onClick={saveDetails} data-testid="save-details-btn" className="text-[10px] font-medium text-white bg-[#205B5A] px-3 py-1 rounded-full hover:bg-[#174544]">Save</button></div>}
                </div>
                {editing ? (
                  <div className="grid md:grid-cols-2 gap-3">
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Title</label><select value={editForm.title || ""} onChange={e => setEditForm({...editForm, title: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]"><option value="">—</option>{["Mr", "Mrs", "Ms", "Dr", "Prof"].map(t => <option key={t}>{t}</option>)}</select></div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">First name</label>{inp("first_name", "First name")}</div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Last name</label>{inp("last_name", "Last name")}</div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Preferred name</label>{inp("preferred_name", "Preferred name")}</div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Date of birth</label>{inp("date_of_birth", "", "date")}</div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Gender</label><select value={editForm.gender || ""} onChange={e => setEditForm({...editForm, gender: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-sm"><option value="">—</option>{["Male", "Female", "Non-binary", "Other"].map(g => <option key={g}>{g}</option>)}</select></div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Email</label>{inp("email", "Email", "email")}</div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Phone</label>{inp("phone", "Phone")}</div>
                    <div className="md:col-span-2"><label className="text-[10px] text-[#757675] mb-1 block">Address</label>{inp("address", "Address")}</div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Postcode</label>{inp("postcode", "Postcode")}</div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Occupation</label>{inp("occupation", "Occupation")}</div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Emergency contact</label>{inp("emergency_contact_name", "Name")}</div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Emergency phone</label>{inp("emergency_contact_phone", "Phone")}</div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Referring doctor</label>{inp("referring_doctor", "GP / Referring doctor")}</div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Referral source</label>{inp("referral_source", "How they found the clinic")}</div>
                    <div><label className="text-[10px] text-[#757675] mb-1 block">Reference number</label>{inp("reference_number", "Ref #")}</div>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    {/* Name - not typically copied */}
                    {`${p.title || ""} ${p.first_name} ${p.last_name}`.trim() && (
                      <div className="flex justify-between py-1 border-b border-[#1A1B1A]/[0.03] group">
                        <span className="text-[#757675]">Name</span>
                        <span className="text-[#1A1B1A] font-medium text-right flex items-center gap-1">
                          {`${p.title || ""} ${p.first_name} ${p.last_name}`.trim()}
                          <CopyButton 
                            value={`${p.title || ""} ${p.first_name} ${p.last_name}`.trim()} 
                            label="Copy patient name" 
                            toastMessage="Name copied"
                            size="xs"
                            showOnHover
                          />
                        </span>
                      </div>
                    )}
                    {/* Preferred name */}
                    {p.preferred_name && (
                      <div className="flex justify-between py-1 border-b border-[#1A1B1A]/[0.03]">
                        <span className="text-[#757675]">Preferred name</span>
                        <span className="text-[#1A1B1A] font-medium text-right">{p.preferred_name}</span>
                      </div>
                    )}
                    {/* DOB - copyable for forms */}
                    {p.date_of_birth && (
                      <div className="flex justify-between py-1 border-b border-[#1A1B1A]/[0.03] group">
                        <span className="text-[#757675]">DOB</span>
                        <span className="text-[#1A1B1A] font-medium text-right flex items-center gap-1">
                          {p.date_of_birth}
                          <CopyButton 
                            value={p.date_of_birth} 
                            label="Copy date of birth" 
                            toastMessage="Date of birth copied"
                            size="xs"
                            showOnHover
                          />
                        </span>
                      </div>
                    )}
                    {/* Gender */}
                    {p.gender && (
                      <div className="flex justify-between py-1 border-b border-[#1A1B1A]/[0.03]">
                        <span className="text-[#757675]">Gender</span>
                        <span className="text-[#1A1B1A] font-medium text-right">{p.gender}</span>
                      </div>
                    )}
                    {/* Email - highly copyable */}
                    {p.email && (
                      <div className="flex justify-between py-1 border-b border-[#1A1B1A]/[0.03] group">
                        <span className="text-[#757675]">Email</span>
                        <span className="text-[#1A1B1A] font-medium text-right flex items-center gap-1">
                          {p.email}
                          <CopyButton 
                            value={p.email} 
                            label="Copy email address" 
                            toastMessage="Email copied"
                            size="xs"
                            showOnHover
                          />
                        </span>
                      </div>
                    )}
                    {/* Phone - highly copyable */}
                    {p.phone && (
                      <div className="flex justify-between py-1 border-b border-[#1A1B1A]/[0.03] group">
                        <span className="text-[#757675]">Phone</span>
                        <span className="text-[#1A1B1A] font-medium text-right flex items-center gap-1">
                          {p.phone}
                          <CopyButton 
                            value={p.phone} 
                            label="Copy phone number" 
                            toastMessage="Phone number copied"
                            size="xs"
                            showOnHover
                          />
                        </span>
                      </div>
                    )}
                    {/* Address - copyable */}
                    {[p.address, p.postcode].filter(Boolean).join(", ") && (
                      <div className="flex justify-between py-1 border-b border-[#1A1B1A]/[0.03] group">
                        <span className="text-[#757675]">Address</span>
                        <span className="text-[#1A1B1A] font-medium text-right flex items-center gap-1">
                          {[p.address, p.postcode].filter(Boolean).join(", ")}
                          <CopyButton 
                            value={[p.address, p.postcode].filter(Boolean).join(", ")} 
                            label="Copy address" 
                            toastMessage="Address copied"
                            size="xs"
                            showOnHover
                          />
                        </span>
                      </div>
                    )}
                    {/* Other fields - less commonly copied */}
                    {p.occupation && (
                      <div className="flex justify-between py-1 border-b border-[#1A1B1A]/[0.03]">
                        <span className="text-[#757675]">Occupation</span>
                        <span className="text-[#1A1B1A] font-medium text-right">{p.occupation}</span>
                      </div>
                    )}
                    {p.emergency_contact_name && (
                      <div className="flex justify-between py-1 border-b border-[#1A1B1A]/[0.03]">
                        <span className="text-[#757675]">Emergency contact</span>
                        <span className="text-[#1A1B1A] font-medium text-right">{p.emergency_contact_name}</span>
                      </div>
                    )}
                    {/* Emergency phone - copyable */}
                    {p.emergency_contact_phone && (
                      <div className="flex justify-between py-1 border-b border-[#1A1B1A]/[0.03] group">
                        <span className="text-[#757675]">Emergency phone</span>
                        <span className="text-[#1A1B1A] font-medium text-right flex items-center gap-1">
                          {p.emergency_contact_phone}
                          <CopyButton 
                            value={p.emergency_contact_phone} 
                            label="Copy emergency phone" 
                            toastMessage="Emergency phone copied"
                            size="xs"
                            showOnHover
                          />
                        </span>
                      </div>
                    )}
                    {p.referring_doctor && (
                      <div className="flex justify-between py-1 border-b border-[#1A1B1A]/[0.03]">
                        <span className="text-[#757675]">Referring doctor</span>
                        <span className="text-[#1A1B1A] font-medium text-right">{p.referring_doctor}</span>
                      </div>
                    )}
                    {p.referral_source && (
                      <div className="flex justify-between py-1 border-b border-[#1A1B1A]/[0.03]">
                        <span className="text-[#757675]">Referral source</span>
                        <span className="text-[#1A1B1A] font-medium text-right">{p.referral_source}</span>
                      </div>
                    )}
                    {/* Ref # - copyable for admin */}
                    {p.reference_number && (
                      <div className="flex justify-between py-1 border-b border-[#1A1B1A]/[0.03] group">
                        <span className="text-[#757675]">Ref #</span>
                        <span className="text-[#1A1B1A] font-medium text-right flex items-center gap-1 font-mono text-xs">
                          {p.reference_number}
                          <CopyButton 
                            value={p.reference_number} 
                            label="Copy reference number" 
                            toastMessage="Reference copied"
                            size="xs"
                            showOnHover
                          />
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Archive: the normal operational action. Permanent erasure lives in
                  Privacy & data rights and is a reviewed workflow, not a button. */}
              <button onClick={() => setShowArchiveConfirm(true)} data-testid="archive-patient-btn" className="text-xs text-[#757675] hover:text-red-500 flex items-center gap-1"><Archive className="w-3 h-3" /> Archive patient</button>
            </div>
          )}

          {/* ── PRIVACY & DATA RIGHTS ── */}
          {tab === "privacy" && (
            <div>
              <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>Privacy &amp; data rights</h2>
              <PrivacyDataRights
                patientId={id}
                patientName={`${p.first_name || ""} ${p.last_name || ""}`.trim()}
                canManage={["owner", "admin"].includes(user?.role)}
              />
            </div>
          )}

          {/* ── TREATMENT NOTES ── */}
          {tab === "notes" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-[#1A1B1A]" style={H}>Treatment notes</h2>
                <div className="flex items-center gap-2">
                  {data.notes?.length > 0 && (
                    <button onClick={async () => { await generateFullHistoryPdf(data.notes, { patient: data.patient, clinic, getTemplateImageFn: getTemplateImage }); }} data-testid="download-full-history-btn"
                      className="border border-[#1A1B1A]/15 text-[#4A4B4A] px-3 py-1.5 rounded-full text-[11px] font-medium hover:bg-[#FAF9F6] flex items-center gap-1.5 transition-colors"><Download className="w-3.5 h-3.5" /> Download full treatment history</button>
                  )}
                  <button onClick={() => { setNoteForm(null); setShowNoteForm(true); }} data-testid="add-note-btn"
                    className="bg-[#205B5A] text-white px-3.5 py-1.5 rounded-full text-[11px] font-medium hover:bg-[#174544] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add treatment note</button>
                </div>
              </div>

              {/* Search/filter */}
              {data.notes?.length > 0 && (
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#757675]" />
                  <input value={noteFilter} onChange={e => setNoteFilter(e.target.value)} data-testid="notes-filter"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 bg-white text-sm focus:outline-none focus:border-[#205B5A]"
                    placeholder="Filter treatment notes by any word or phrase..." />
                </div>
              )}

              {data.notes?.length === 0 ? (
                <div className="bg-[#205B5A]/[0.03] rounded-2xl border border-[#205B5A]/10 p-8 text-center">
                  <Mic className="w-8 h-8 text-[#205B5A]/30 mx-auto mb-2" />
                  <p className="text-sm text-[#757675]">No treatment notes yet</p>
                  <p className="text-xs text-[#757675] mt-1">Click &quot;Add treatment note&quot; to start documenting.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {data.notes.filter(n => {
                    if (!noteFilter) return true;
                    const q = noteFilter.toLowerCase();
                    return (n.appointment_type || "").toLowerCase().includes(q) ||
                      (n.practitioner_name || "").toLowerCase().includes(q) ||
                      noteDisplayFields(n).some(f =>
                        f.label.toLowerCase().includes(q) || String(f.value).toLowerCase().includes(q));
                  }).map(n => (
                    <div key={n.id} data-testid={`note-card-${n.id}`}
                      className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden hover:shadow-sm transition-shadow">
                      {/* Note card header */}
                      <div className="px-5 py-3.5 flex items-center justify-between border-b border-[#1A1B1A]/[0.04]">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[#205B5A]/[0.06] flex items-center justify-center flex-shrink-0">
                            <FileText className="w-4 h-4 text-[#205B5A]" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-[#1A1B1A]">{n.appointment_type || "Treatment note"}</p>
                            <p className="text-[10px] text-[#757675]">{n.practitioner_name} · {n.created_at?.split("T")[0]}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[9px] font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1 ${
                            n.status === "final" ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-[#496275]/10 text-[#496275]"
                          }`}>
                            {n.status === "final" ? <><Lock className="w-2.5 h-2.5" /> Final</> : "Draft"}
                          </span>
                          {n.status === "draft" ? (
                            <button onClick={() => { setNoteForm(n); setShowNoteForm(true); }} data-testid={`edit-note-${n.id}`}
                              className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544] px-2 py-1 rounded-lg hover:bg-[#205B5A]/5">Edit</button>
                          ) : (
                            <button onClick={() => { setNoteForm(n); setShowNoteForm(true); }} data-testid={`view-note-${n.id}`}
                              className="text-[10px] font-medium text-[#4A4B4A] hover:text-[#1A1B1A] px-2 py-1 rounded-lg hover:bg-[#FAF9F6] flex items-center gap-0.5"><Eye className="w-3 h-3" /> View</button>
                          )}
                        </div>
                      </div>
                      {/* Note card content - ALL populated fields, full text */}
                      <div className="px-5 py-3 space-y-2.5">
                        {noteDisplayFields(n).map(({ key, label, value }) => (
                          <div key={key}>
                            <p className="text-[10px] font-bold text-[#1A1B1A] mb-0.5">{label}</p>
                            <p className="text-xs text-[#4A4B4A] leading-relaxed whitespace-pre-wrap">{value}</p>
                          </div>
                        ))}
                        {n.body_charts?.length > 0 && (
                          <div className="flex gap-3 pt-2 overflow-x-auto">
                            {n.body_charts.map((chart, ci) => (
                              <div key={ci} className="flex-shrink-0" onClick={e => { e.stopPropagation(); setLightboxChart(chart); }}>
                                <BodyChartPreview chart={chart} size="md" onClick={() => setLightboxChart(chart)} />
                              </div>
                            ))}
                          </div>
                        )}
                        {n.body_regions?.length > 0 && !n.body_charts?.length && <div className="flex gap-1 pt-1">{n.body_regions.map(r => <span key={r} className="text-[9px] bg-[#FAF9F6] px-2 py-0.5 rounded-full text-[#757675]">{r}</span>)}</div>}
                      </div>
                      {/* Note actions */}
                      <div className="px-5 py-2.5 border-t border-[#1A1B1A]/[0.04] flex items-center gap-3">
                        <button onClick={e => { e.stopPropagation(); setEmailModal({ note: n, patient: data.patient }); }} data-testid={`email-note-${n.id}`}
                          className="text-[10px] font-medium text-[#4A4B4A] hover:text-[#205B5A] flex items-center gap-1 transition-colors"><Mail className="w-3 h-3" /> Email</button>
                        <button onClick={async e => { e.stopPropagation();
                          await generateNotePdf(n, { patient: data.patient, clinic, getTemplateImageFn: getTemplateImage });
                        }} data-testid={`pdf-note-${n.id}`}
                          className="text-[10px] font-medium text-[#4A4B4A] hover:text-[#205B5A] flex items-center gap-1 transition-colors"><Download className="w-3 h-3" /> PDF</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── APPOINTMENTS ── */}
          {tab === "appointments" && (
            <div>
              <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>Appointments</h2>
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
                {data.appointments?.length === 0 ? <p className="p-8 text-center text-sm text-[#757675]">No appointments</p> : (
                  <table className="w-full"><thead><tr className="border-b border-[#1A1B1A]/[0.06]"><th className="text-left px-4 py-2.5 text-[10px] font-medium text-[#757675] uppercase">Date</th><th className="text-left px-4 py-2.5 text-[10px] font-medium text-[#757675] uppercase">Time</th><th className="text-left px-4 py-2.5 text-[10px] font-medium text-[#757675] uppercase hidden md:table-cell">Practitioner</th><th className="text-left px-4 py-2.5 text-[10px] font-medium text-[#757675] uppercase hidden md:table-cell">Type</th><th className="text-left px-4 py-2.5 text-[10px] font-medium text-[#757675] uppercase">Status</th></tr></thead>
                  <tbody>{data.appointments.map(a => (
                    <tr key={a.id} className="border-b border-[#1A1B1A]/[0.03] hover:bg-[#FAF9F6] cursor-pointer" onClick={() => navigate(`/app/appointments/${a.id}`)}>
                      <td className="px-4 py-2.5 text-sm text-[#1A1B1A]">{a.date}</td><td className="px-4 py-2.5 text-sm text-[#757675] font-mono">{a.start_time}</td>
                      <td className="px-4 py-2.5 text-sm text-[#757675] hidden md:table-cell">{a.practitioner_name}</td><td className="px-4 py-2.5 text-sm text-[#757675] hidden md:table-cell">{a.appointment_type_name}</td>
                      <td className="px-4 py-2.5"><span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full capitalize ${a.status === "completed" ? "bg-[#205B5A]/10 text-[#205B5A]" : a.status === "cancelled" ? "bg-red-50 text-red-500" : "bg-[#1A1B1A]/[0.04] text-[#757675]"}`}>{a.status}</span></td>
                    </tr>
                  ))}</tbody></table>
                )}
              </div>
            </div>
          )}

          {/* ── INVOICES ── */}
          {tab === "invoices" && (
            <div>
              <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>Invoices</h2>
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
                {data.invoices?.length === 0 ? <p className="p-8 text-center text-sm text-[#757675]">No invoices</p> : (
                  <table className="w-full"><thead><tr className="border-b border-[#1A1B1A]/[0.06]"><th className="text-left px-4 py-2.5 text-[10px] font-medium text-[#757675] uppercase">Invoice</th><th className="text-right px-4 py-2.5 text-[10px] font-medium text-[#757675] uppercase">Total</th><th className="text-left px-4 py-2.5 text-[10px] font-medium text-[#757675] uppercase">Status</th></tr></thead>
                  <tbody>{data.invoices.map(inv => (
                    <tr key={inv.id} className="border-b border-[#1A1B1A]/[0.03] hover:bg-[#FAF9F6] cursor-pointer group" onClick={() => navigate(`/app/invoices/${inv.id}`)}>
                      <td className="px-4 py-2.5 text-sm font-medium text-[#1A1B1A]">
                        <span className="flex items-center gap-1">
                          {inv.invoice_number}
                          <CopyButton 
                            value={inv.invoice_number} 
                            label="Copy invoice number" 
                            toastMessage="Invoice number copied"
                            size="xs"
                            showOnHover
                          />
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-sm font-semibold text-[#1A1B1A] text-right">£{inv.total?.toFixed(2)}</td>
                      <td className="px-4 py-2.5"><span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${inv.status === "paid" ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-amber-50 text-amber-600"}`}>{inv.status}</span></td>
                    </tr>
                  ))}</tbody></table>
                )}
              </div>
            </div>
          )}

          {/* ── ACCOUNT STATEMENT ── */}
          {tab === "account" && (
            <div>
              <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>Account statement</h2>
              <div className="grid grid-cols-3 gap-4 mb-5">
                <div className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-4"><p className="text-[10px] text-[#757675] uppercase tracking-wider mb-1">Total invoiced</p><p className="text-xl font-medium text-[#1A1B1A]" style={H}>£{data.account?.total_invoiced?.toFixed(2)}</p></div>
                <div className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-4"><p className="text-[10px] text-[#757675] uppercase tracking-wider mb-1">Total paid</p><p className="text-xl font-medium text-[#205B5A]" style={H}>£{data.account?.total_paid?.toFixed(2)}</p></div>
                <div className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-4"><p className="text-[10px] text-[#757675] uppercase tracking-wider mb-1">Balance</p><p className={`text-xl font-medium ${data.account?.balance > 0 ? "text-amber-600" : "text-[#205B5A]"}`} style={H}>£{data.account?.balance?.toFixed(2)}</p></div>
              </div>
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
                <table className="w-full"><thead><tr className="border-b border-[#1A1B1A]/[0.06]"><th className="text-left px-4 py-2.5 text-[10px] font-medium text-[#757675] uppercase">Date</th><th className="text-left px-4 py-2.5 text-[10px] font-medium text-[#757675] uppercase">Description</th><th className="text-right px-4 py-2.5 text-[10px] font-medium text-[#757675] uppercase">Amount</th><th className="text-left px-4 py-2.5 text-[10px] font-medium text-[#757675] uppercase">Status</th></tr></thead>
                <tbody>{data.invoices?.map(inv => (
                  <tr key={inv.id} className="border-b border-[#1A1B1A]/[0.03]"><td className="px-4 py-2 text-xs text-[#757675]">{inv.created_at?.split("T")[0]}</td><td className="px-4 py-2 text-xs text-[#1A1B1A]">{inv.invoice_number}</td><td className="px-4 py-2 text-xs font-medium text-[#1A1B1A] text-right">£{inv.total?.toFixed(2)}</td><td className="px-4 py-2"><span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${inv.status === "paid" ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-amber-50 text-amber-600"}`}>{inv.status}</span></td></tr>
                ))}</tbody></table>
              </div>
            </div>
          )}

          {/* ── FORMS (dedicated section) ── */}
          {tab === "forms" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-[#1A1B1A]" style={H}>Forms</h2>
              </div>
              {data.form_submissions?.length === 0 ? (
                <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-8 text-center">
                  <FileCheck className="w-8 h-8 text-[#757675]/20 mx-auto mb-2" />
                  <p className="text-sm text-[#757675] mb-1">No completed forms</p>
                  <p className="text-xs text-[#757675]">Submitted consent forms, intake forms, and signed documents will appear here automatically.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
                  {data.form_submissions.map(fs => (
                    <div key={fs.id} className="flex items-center gap-3 px-5 py-3.5 border-b border-[#1A1B1A]/[0.03] last:border-0 hover:bg-[#FAF9F6] transition-colors cursor-pointer"
                      data-testid={`form-submission-${fs.id}`}
                      onClick={() => openFormSubmission(fs.id)}>
                      <div className="w-8 h-8 rounded-lg bg-[#205B5A]/[0.06] flex items-center justify-center flex-shrink-0">
                        <FileCheck className="w-4 h-4 text-[#205B5A]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A1B1A]">{fs.form_name || "Form"}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full capitalize ${
                            { consent: "bg-[#205B5A]/10 text-[#205B5A]", intake: "bg-[#496275]/10 text-[#496275]", treatment_info: "bg-amber-50 text-amber-600", privacy: "bg-blue-50 text-blue-600" }[fs.form_type] || "bg-[#1A1B1A]/5 text-[#757675]"
                          }`}>{fs.form_type || "form"}</span>
                          <span className="text-[10px] text-[#757675]">{fs.created_at?.split("T")[0]}</span>
                          {fs.appointment_id && <span className="text-[10px] text-[#757675]">· linked to appointment</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-[#205B5A]/10 text-[#205B5A]" data-testid={`form-submitted-badge-${fs.id}`}>Submitted</span>
                        <FormReviewBadge status={fs.review_status} testId={`form-review-badge-${fs.id}`} />
                        {(fs.signature || fs.signature_data || fs.typed_signature) && (
                          <span className="text-[9px] font-semibold text-[#205B5A] bg-[#205B5A]/10 px-2 py-0.5 rounded-full flex items-center gap-0.5"><CheckCircle2 className="w-2.5 h-2.5" /> Signed</span>
                        )}
                        <ChevronRight className="w-3.5 h-3.5 text-[#757675]" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}


          {/* ── LETTERS ── */}
          {tab === "letters" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-[#1A1B1A]" style={H}>Letters & emails</h2>
                <button
                  onClick={() => setShowComposer(true)}
                  data-testid="new-letter-btn"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#205B5A] text-white text-xs font-medium hover:bg-[#174544] transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" /> New letter
                </button>
              </div>
              {data.letters?.length === 0 ? (
                <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-8 text-center">
                  <Mail className="w-8 h-8 text-[#757675]/20 mx-auto mb-2" />
                  <p className="text-sm text-[#757675]">No letters or emails sent yet</p>
                  <p className="text-xs text-[#757675] mt-1">Emails sent from treatment notes will appear here automatically.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.letters.map(l => (
                    <div key={l.id} className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden" data-testid={`letter-${l.id}`}>
                      <div className={`px-5 py-3.5 ${l.status !== "sent" ? "cursor-pointer hover:bg-[#FAF9F6] transition-colors" : ""}`}
                        onClick={l.status !== "sent" ? (e) => { if (e.target.closest("button")) return; setEditLetter(l); } : undefined}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5 text-[#205B5A]" />
                            <p className="text-sm font-medium text-[#1A1B1A]">{l.subject}</p>
                          </div>
                          <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${l.status === "sent" ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-amber-50 text-amber-600"}`}>{l.status}</span>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-[#757675] mb-2">
                          <span>To: {l.recipient_email}</span>
                          <span>·</span>
                          <span>{l.practitioner_name}</span>
                          <span>·</span>
                          <span>{l.status === "sent"
                            ? `Sent ${l.sent_at?.split("T")[0]?.split("-").reverse().join("/") || ""}${l.sent_at?.split("T")[1] ? `, ${l.sent_at.split("T")[1].slice(0, 5)}` : ""}`
                            : `Not sent · last edited ${(l.updated_at || l.created_at || "").split("T")[0]?.split("-").reverse().join("/")}`}</span>
                        </div>
                        {l.cc_emails?.length > 0 && (
                          <p className="text-[10px] text-[#757675] mb-2">CC: {l.cc_emails.join(", ")}</p>
                        )}
                        {l.attachments?.length > 0 && (
                          <div className="flex items-center gap-1.5 text-[10px] text-[#205B5A] bg-[#205B5A]/[0.04] rounded-lg px-2.5 py-1 w-fit mb-2">
                            <FileText className="w-3 h-3" /> {l.attachments.length} attachment{l.attachments.length > 1 ? "s" : ""}
                          </div>
                        )}
                        {l.attachment_type === "pdf" && (
                          <div className="flex items-center gap-1.5 text-[10px] text-[#205B5A] bg-[#205B5A]/[0.04] rounded-lg px-2.5 py-1 w-fit mb-2">
                            <FileText className="w-3 h-3" /> Treatment note PDF attached
                          </div>
                        )}
                        <div className="bg-[#FAF9F6] rounded-lg p-3 text-xs text-[#4A4B4A] leading-relaxed whitespace-pre-wrap line-clamp-3">{l.message_body}</div>
                        <div className="flex flex-wrap gap-2 mt-2.5">
                          {l.status !== "sent" && (
                            <>
                              <button
                                onClick={() => setEditLetter(l)}
                                data-testid={`edit-letter-${l.id}`}
                                className="text-[10px] font-medium text-white bg-[#205B5A] hover:bg-[#174544] px-2.5 py-1 rounded-lg flex items-center gap-1"
                              >
                                <Pencil className="w-3 h-3" /> Edit draft
                              </button>
                              <button
                                onClick={() => setDeletingLetter(l)}
                                data-testid={`delete-letter-${l.id}`}
                                className="text-[10px] font-medium text-red-600 hover:bg-red-50 px-2 py-1 rounded-lg flex items-center gap-1"
                              >
                                <Trash2 className="w-3 h-3" /> Delete draft
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setViewLetter(l)}
                            data-testid={`view-letter-${l.id}`}
                            className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544] px-2 py-1 rounded-lg hover:bg-[#205B5A]/5 flex items-center gap-1"
                          >
                            <Eye className="w-3 h-3" /> View details
                          </button>
                          {l.body_html && (
                            <button
                              onClick={() => downloadLetterPdf(l)}
                              data-testid={`download-letter-${l.id}`}
                              className="text-[10px] font-medium text-[#4A4B4A] hover:text-[#1A1B1A] px-2 py-1 rounded-lg hover:bg-[#FAF9F6] flex items-center gap-1"
                            >
                              <Download className="w-3 h-3" /> Download PDF
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}


          {/* ── FILES (clinical documents only) ── */}
          {tab === "files" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-[#1A1B1A]" style={H}>Files</h2>
                <label className="bg-[#205B5A] text-white px-3.5 py-1.5 rounded-full text-[11px] font-medium hover:bg-[#174544] flex items-center gap-1 cursor-pointer" data-testid="upload-file-btn"><Upload className="w-3.5 h-3.5" /> Upload file<input type="file" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append("file", f); fd.append("patient_id", id); await api.post("/files", fd); load(); }} /></label>
              </div>
              <p className="text-xs text-[#757675] mb-4">Upload clinical documents such as doctor reports, scan results, blood tests, and other medical attachments.</p>
              {data.files?.length === 0 ? (
                <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-8 text-center">
                  <FolderOpen className="w-8 h-8 text-[#757675]/20 mx-auto mb-2" />
                  <p className="text-sm text-[#757675]">No files uploaded</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">{data.files.map(f => (
                  <div key={f.id} className="flex items-center gap-3 px-4 py-3 border-b border-[#1A1B1A]/[0.03] last:border-0 hover:bg-[#FAF9F6]">
                    <FolderOpen className="w-4 h-4 text-[#496275]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1A1B1A] truncate">{f.filename}</p>
                      <p className="text-[10px] text-[#757675]">{(f.size_bytes / 1024).toFixed(0)} KB · {f.category || "general"}</p>
                    </div>
                    <span className="text-[10px] text-[#757675]">{f.uploaded_at?.split("T")[0]}</span>
                  </div>
                ))}</div>
              )}
            </div>
          )}

          {/* ── CONSENT ── */}
          {tab === "consent" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-[#1A1B1A]" style={H}>Consent tracking</h2>
                {patientConsents.pending_count > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">{patientConsents.pending_count} pending</span>
                )}
              </div>
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5 space-y-3">
                {patientConsents.consents.length === 0 && consentTypes.length === 0 && (
                  <p className="text-sm text-[#757675] py-4 text-center">No consent types configured. Go to Settings → Consent to add consent requirements.</p>
                )}
                {patientConsents.consents.map(ct => {
                  const status = ct.status || "pending";
                  const statusClass = status === "accepted" ? "bg-emerald-50 border-emerald-200" : status === "rejected" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200";
                  return (
                    <div key={ct.consent_type_id} className={`flex items-center justify-between py-3 px-4 rounded-xl border ${statusClass}`} data-testid={`consent-${ct.consent_type_id}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[#1A1B1A]">{ct.consent_type_name}</p>
                          {ct.is_marketing_consent && (
                            <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600">Marketing</span>
                          )}
                          {ct.is_required && status !== "accepted" && (
                            <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">Required</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {ct.updated_at && <p className="text-[10px] text-[#757675]">Updated {ct.updated_at.split("T")[0]}</p>}
                          {ct.source_form_id && (
                            <button onClick={async () => {
                              setViewFormLoading(true);
                              try {
                                const { data: sub } = await api.get(`/forms/submissions/${ct.source_form_id}`);
                                setViewFormSubmission(sub);
                              } catch { }
                              setViewFormLoading(false);
                            }} className="text-[10px] text-[#205B5A] hover:underline">View supporting document</button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${status === "accepted" ? "bg-emerald-500 text-white" : status === "rejected" ? "bg-red-500 text-white" : "bg-amber-400 text-white"}`}>
                          {status === "accepted" ? "Accepted" : status === "rejected" ? "Rejected" : "Pending"}
                        </span>
                        <div className="flex gap-1">
                          {status !== "accepted" && (
                            <button onClick={() => updateConsent(ct.consent_type_id, "accepted")} className="text-[10px] font-medium px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Accept</button>
                          )}
                          {status !== "rejected" && (
                            <button onClick={() => updateConsent(ct.consent_type_id, "rejected")} className="text-[10px] font-medium px-2.5 py-1 rounded-full bg-red-100 text-red-700 hover:bg-red-200">Reject</button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── TIMELINE ── */}
          {tab === "timeline" && (
            <div>
              <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>Timeline</h2>
              {data.timeline?.length === 0 ? <p className="text-sm text-[#757675] py-8 text-center">No events yet</p> : (
                <div className="space-y-0">{data.timeline.map((ev, i) => {
                  const TlIcon = TIMELINE_ICONS[ev.event_type] || Clock;
                  return (
                    <div key={ev.id} className="flex gap-3 py-3">
                      <div className="flex flex-col items-center"><div className="w-7 h-7 rounded-full bg-[#205B5A]/8 flex items-center justify-center"><TlIcon className="w-3 h-3 text-[#205B5A]" /></div>{i < data.timeline.length - 1 && <div className="w-px flex-1 bg-[#1A1B1A]/[0.06] mt-1" />}</div>
                      <div className="pb-2"><p className="text-sm text-[#1A1B1A]">{ev.description}</p><p className="text-[10px] text-[#757675]">{timeAgo(ev.created_at)}</p></div>
                    </div>
                  );
                })}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── MODALS ── */}
      {showAddAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4"><div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
          <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>Add medical alert</h2>
          <form onSubmit={addAlert} className="space-y-3">
            <input value={alertForm.alert_type} onChange={e => setAlertForm({...alertForm, alert_type: e.target.value})} required placeholder="Alert type (e.g. Allergy)" className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
            <textarea value={alertForm.description} onChange={e => setAlertForm({...alertForm, description: e.target.value})} required placeholder="Description" rows={2} className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none" />
            <select value={alertForm.severity} onChange={e => setAlertForm({...alertForm, severity: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select>
            <div className="flex justify-end gap-3 pt-1"><button type="button" onClick={() => setShowAddAlert(false)} className="text-sm text-[#757675]">Cancel</button><button type="submit" className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544]">Add alert</button></div>
          </form>
        </div></div>
      )}

      {showAddFlag && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4"><div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
          <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>Add patient flag</h2>
          <input value={flagLabel} onChange={e => setFlagLabel(e.target.value)} placeholder="Flag label (e.g. VIP, Interpreter needed)" className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] mb-3" />
          <div className="flex justify-end gap-3"><button onClick={() => setShowAddFlag(false)} className="text-sm text-[#757675]">Cancel</button><button onClick={addFlag} className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544]">Add flag</button></div>
        </div></div>
      )}

      {showNoteForm && (
        <TreatmentNoteEditor
          patientId={id}
          patientData={data}
          existingNote={noteForm}
          onClose={() => { setShowNoteForm(false); setNoteForm(null); }}
          onSaved={() => { setShowNoteForm(false); setNoteForm(null); load(); }}
        />
      )}

      {/* Lightbox for body chart viewing from note cards */}
      {lightboxChart && <BodyChartLightbox chart={lightboxChart} onClose={() => setLightboxChart(null)} />}

      {/* ── Email Note Modal ── */}
      {emailModal && (
        <EmailNoteModal note={emailModal.note} patient={emailModal.patient} clinic={clinic} user={user} onClose={() => setEmailModal(null)} />
      )}

      {/* ── Submitted Form Viewer ── */}
      {(showComposer || editLetter) && (
        <LetterComposer
          key={editLetter?.id || "new-letter"}
          patient={data.patient}
          clinic={clinic}
          user={user}
          letter={editLetter}
          onClose={() => { setShowComposer(false); setEditLetter(null); }}
          onSent={(saved) => { load(); if (saved?.status === "sent") { setShowComposer(false); setEditLetter(null); } }}
        />
      )}

      {deletingLetter && (
        <div className="fixed inset-0 z-[70] bg-[#1A1B1A]/50 flex items-center justify-center p-4" data-testid="delete-letter-dialog">
          <div className="bg-white rounded-2xl max-w-sm w-full p-5">
            <h3 className="text-base text-[#1A1B1A] mb-1" style={H}>Delete this draft?</h3>
            <p className="text-xs text-[#757675] mb-4">
              "{deletingLetter.subject}" has not been emailed to the patient. This cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeletingLetter(null)} data-testid="delete-letter-cancel-btn"
                className="px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-xs text-[#1A1B1A] hover:bg-[#F3F2EE]">Keep draft</button>
              <button data-testid="delete-letter-confirm-btn"
                onClick={async () => {
                  try { await api.delete(`/letters/${deletingLetter.id}`); } catch (e) { /* surfaced by reload */ }
                  setDeletingLetter(null);
                  load();
                }}
                className="px-3 py-2 rounded-xl bg-red-600 text-white text-xs font-medium hover:bg-red-700">Delete draft</button>
            </div>
          </div>
        </div>
      )}

      {viewLetter && (
        <div className="fixed inset-0 z-[60] bg-[#1A1B1A]/50 flex items-center justify-center p-4" data-testid="letter-detail-view" onClick={() => setViewLetter(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[#1A1B1A]/[0.06] flex items-start gap-3">
              <div className="min-w-0">
                <h3 className="text-base text-[#1A1B1A]" style={H}>{viewLetter.subject}</h3>
                <p className="text-[10px] text-[#757675] mt-0.5">
                  {viewLetter.status} · {viewLetter.sent_by_name || viewLetter.practitioner_name} ·{" "}
                  {(viewLetter.sent_at || viewLetter.created_at || "").replace("T", " ").slice(0, 16)}
                </p>
              </div>
              <button onClick={() => setViewLetter(null)} data-testid="letter-detail-close-btn" className="ml-auto p-1.5 rounded-lg hover:bg-[#FAF9F6] text-[#4A4B4A]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="space-y-1 text-xs">
                <div className="flex gap-2"><span className="text-[#757675] w-24">To</span><span className="text-[#1A1B1A]" data-testid="letter-detail-to">{viewLetter.recipient_email}</span></div>
                {viewLetter.cc_emails?.length > 0 && (
                  <div className="flex gap-2"><span className="text-[#757675] w-24">CC</span><span className="text-[#1A1B1A]" data-testid="letter-detail-cc">{viewLetter.cc_emails.join(", ")}</span></div>
                )}
                <div className="flex gap-2"><span className="text-[#757675] w-24">Status</span><span className="text-[#1A1B1A]">{viewLetter.status}</span></div>
                {viewLetter.email_logs?.map((log, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="text-[#757675] w-24">{log.recipient_email === viewLetter.recipient_email ? "Delivery" : "CC delivery"}</span>
                    <span className="text-[#1A1B1A]">
                      {log.status}{log.provider_message_id ? ` · ${log.provider_message_id.slice(0, 12)}` : ""}
                      {log.failure_reason ? ` · ${log.failure_reason}` : ""}
                    </span>
                  </div>
                ))}
              </div>
              {viewLetter.attachments?.length > 0 && (
                <div className="space-y-1" data-testid="letter-detail-attachments">
                  {viewLetter.attachments.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs bg-[#FAF9F6] rounded-lg px-3 py-2">
                      <FileText className="w-3 h-3 text-[#205B5A]" />
                      <span className="truncate">{a.filename}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t border-[#1A1B1A]/[0.06] pt-3">
                {viewLetter.body_html ? (
                  <div className="text-sm text-[#1A1B1A] leading-relaxed [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-1 [&_p]:mb-2 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5"
                    data-testid="letter-detail-body" dangerouslySetInnerHTML={{ __html: viewLetter.body_html }} />
                ) : (
                  <p className="text-sm text-[#4A4B4A] whitespace-pre-wrap" data-testid="letter-detail-body">{viewLetter.message_body}</p>
                )}
              </div>
              {viewLetter.body_html && (
                <button onClick={() => downloadLetterPdf(viewLetter)} data-testid="letter-detail-download-btn"
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-xs text-[#1A1B1A] hover:bg-[#FAF9F6]">
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {fullFormSubmission && (
        <SubmittedFormViewer
          submission={fullFormSubmission}
          onClose={() => setFullFormSubmission(null)}
          onReviewed={(updated) => { setViewFormSubmission(v => (v ? { ...v, ...updated } : v)); load(); }}
        />
      )}

      {viewFormSubmission && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="form-viewer">
          <div className="absolute inset-0 bg-black/20" onClick={() => setViewFormSubmission(null)} />
          <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-slide-in-right">
            <div className="sticky top-0 bg-white border-b border-[#1A1B1A]/[0.06] px-5 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-base font-medium text-[#1A1B1A]" style={H}>{viewFormSubmission.template?.name || "Submitted form"}</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full capitalize ${
                    { consent: "bg-[#205B5A]/10 text-[#205B5A]", intake: "bg-[#496275]/10 text-[#496275]" }[viewFormSubmission.template?.form_type] || "bg-[#1A1B1A]/5 text-[#757675]"
                  }`}>{viewFormSubmission.template?.form_type || "form"}</span>
                  {viewFormSubmission.signature && <span className="text-[9px] font-semibold text-[#205B5A] bg-[#205B5A]/10 px-2 py-0.5 rounded-full flex items-center gap-0.5"><CheckCircle2 className="w-2.5 h-2.5" /> Signed</span>}
                </div>
              </div>
              <button onClick={() => setViewFormSubmission(null)} className="p-1 text-[#757675] hover:text-[#1A1B1A]"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-5 space-y-5">
              {/* Metadata */}
              <div className="bg-[#FAF9F6] rounded-xl p-4 space-y-2">
                {viewFormSubmission.patient_name && <div className="flex items-center justify-between text-xs"><span className="text-[#757675]">Patient</span><span className="font-medium text-[#1A1B1A]">{viewFormSubmission.patient_name}</span></div>}
                <div className="flex items-center justify-between text-xs"><span className="text-[#757675]">Submitted</span><span className="font-medium text-[#1A1B1A]">{viewFormSubmission.created_at?.split("T")[0]} at {viewFormSubmission.created_at?.split("T")[1]?.slice(0, 5)}</span></div>
                {viewFormSubmission.appointment_id && <div className="flex items-center justify-between text-xs"><span className="text-[#757675]">Appointment</span><span className="font-medium text-[#205B5A]">Linked</span></div>}
                <div className="flex items-center justify-between text-xs"><span className="text-[#757675]">Status</span><span className="font-medium text-[#1A1B1A] capitalize">{viewFormSubmission.status}</span></div>
              </div>

              {/* Primary actions - the submitted form itself stays viewable after review */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFullFormSubmission(viewFormSubmission)}
                  data-testid="view-submitted-form-btn"
                  className="flex-1 min-w-[150px] text-xs font-medium px-3 py-2.5 rounded-xl bg-[#205B5A] text-white hover:bg-[#174544] transition-colors flex items-center justify-center gap-1.5"
                >
                  <Eye className="w-3.5 h-3.5" /> View submitted form
                </button>
                <button
                  onClick={() => downloadSubmissionPdf(viewFormSubmission)}
                  data-testid="side-download-submission-pdf-btn"
                  className="flex-1 min-w-[130px] text-xs font-medium px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-[#1A1B1A] hover:bg-[#FAF9F6] transition-colors flex items-center justify-center gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </button>
              </div>
              {submissionPdfError && (
                <p className="text-[10px] text-red-500" data-testid="side-submission-pdf-error">{submissionPdfError}</p>
              )}

              {/* Rendered form fields with responses */}
              <div className="space-y-3">
                {viewFormSubmission.template?.fields?.map((field, i) => {
                  const response = viewFormSubmission.responses?.[field.id];
                  return (
                    <div key={field.id || i}>
                      {field.field_type === "heading" && (
                        <h3 className="text-base font-medium text-[#1A1B1A] mt-3 mb-1 border-b border-[#1A1B1A]/[0.06] pb-2" style={H}>{field.label}</h3>
                      )}
                      {field.field_type === "info_text" && (
                        <p className="text-xs text-[#4A4B4A] leading-relaxed bg-[#F3F2EE] rounded-lg px-4 py-3">{field.label}</p>
                      )}
                      {(field.field_type === "short_text" || field.field_type === "long_text") && (
                        <div>
                          <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider block mb-1">{field.label}</label>
                          <div className="px-3 py-2.5 rounded-xl bg-[#FAF9F6] border border-[#1A1B1A]/[0.04] text-sm text-[#1A1B1A] min-h-[36px]">
                            {response || <span className="text-[#757675] italic">Not answered</span>}
                          </div>
                        </div>
                      )}
                      {field.field_type === "date" && (
                        <div>
                          <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider block mb-1">{field.label}</label>
                          <div className="px-3 py-2.5 rounded-xl bg-[#FAF9F6] border border-[#1A1B1A]/[0.04] text-sm text-[#1A1B1A]">
                            {response || <span className="text-[#757675] italic">Not provided</span>}
                          </div>
                        </div>
                      )}
                      {(field.field_type === "dropdown" || field.field_type === "radio") && (
                        <div>
                          <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider block mb-1">{field.label}</label>
                          <div className="px-3 py-2.5 rounded-xl bg-[#FAF9F6] border border-[#1A1B1A]/[0.04] text-sm text-[#1A1B1A]">
                            {response || <span className="text-[#757675] italic">Not selected</span>}
                          </div>
                        </div>
                      )}
                      {field.field_type === "checkbox" && (
                        <div className="flex items-center gap-2.5 px-3 py-2">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${response ? "bg-[#205B5A] border-[#205B5A]" : "border-[#1A1B1A]/20"}`}>
                            {response && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </div>
                          <span className="text-sm text-[#1A1B1A]">{field.label}</span>
                        </div>
                      )}
                      {field.field_type === "signature" && (
                        <div>
                          <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider block mb-1">{field.label}</label>
                          <div className="px-4 py-4 rounded-xl border border-dashed border-[#1A1B1A]/15 bg-[#FAF9F6]">
                            {viewFormSubmission.signature ? (
                              <p className="text-xl italic text-[#1A1B1A]" style={{ fontFamily: "cursive" }}>{viewFormSubmission.signature}</p>
                            ) : (
                              <p className="text-sm text-[#757675] italic">No signature provided</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Consent Review Section - shown for consent forms */}
              {(viewFormSubmission.template?.form_type === "consent" || viewFormSubmission.template?.name?.toLowerCase().includes("consent")) && (
                <div className="border border-[#205B5A]/20 rounded-xl bg-[#205B5A]/[0.03] p-4" data-testid="consent-review-section">
                  <p className="text-xs font-semibold text-[#1A1B1A] mb-2" style={H}>Consent review</p>
                  {viewFormSubmission.consent_reviewed ? (
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${viewFormSubmission.consent_status === "accepted" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
                        {viewFormSubmission.consent_status === "accepted" ? "Accepted" : "Rejected"}
                      </span>
                      <span className="text-[10px] text-[#757675]">Reviewed {(viewFormSubmission.consent_reviewed_at || "")?.split("T")[0]}</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[10px] text-[#757675]">Review this consent form and record the decision.</p>
                      <div className="flex items-center gap-2">
                        <select id="consent-type-select" className="text-xs px-2 py-1.5 rounded-lg border border-[#1A1B1A]/10 bg-white flex-1"
                          data-testid="consent-type-select">
                          <option value="">Select consent type...</option>
                          {consentTypes.map(ct => (
                            <option key={ct.id} value={ct.id}>{ct.name}{ct.is_marketing_consent ? " (Marketing)" : ""}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={async () => {
                          const ctId = document.getElementById("consent-type-select")?.value;
                          if (!ctId) { alert("Please select a consent type"); return; }
                          try {
                            await api.post(`/forms/submissions/${viewFormSubmission.id}/review-consent`, { consent_status: "accepted", consent_type_id: ctId });
                            loadConsents();
                            setViewFormSubmission(null);
                            load();
                          } catch (e) { alert(e.response?.data?.detail || "Error reviewing consent"); }
                        }} data-testid="accept-consent-btn"
                          className="flex-1 text-[11px] font-medium px-3 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600">Accept consent</button>
                        <button onClick={async () => {
                          const ctId = document.getElementById("consent-type-select")?.value;
                          if (!ctId) { alert("Please select a consent type"); return; }
                          try {
                            await api.post(`/forms/submissions/${viewFormSubmission.id}/review-consent`, { consent_status: "rejected", consent_type_id: ctId });
                            loadConsents();
                            setViewFormSubmission(null);
                            load();
                          } catch (e) { alert(e.response?.data?.detail || "Error reviewing consent"); }
                        }} data-testid="reject-consent-btn"
                          className="flex-1 text-[11px] font-medium px-3 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600">Reject consent</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Parent / guardian consent (under-18 patients) */}
              <GuardianDetailsPanel submission={viewFormSubmission} />

              {/* Staff review: accept / reject the submitted form */}
              <FormReviewPanel
                submission={viewFormSubmission}
                onReviewed={(updated) => { setViewFormSubmission({ ...viewFormSubmission, ...updated }); load(); }}
              />

              {/* Read-only notice */}
              <div className="bg-[#FAF9F6] rounded-lg px-4 py-2.5 flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-[#757675]" />
                <span className="text-[10px] text-[#757675]">This is a read-only record of the submitted form.</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showArchiveConfirm && (
        <div className="fixed inset-0 z-50 bg-[#1A1B1A]/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowArchiveConfirm(false)} data-testid="archive-confirm-overlay">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}
            data-testid="archive-confirm-modal">
            <h3 className="text-base font-medium text-[#1A1B1A]" style={H}>Archive patient?</h3>
            <p className="text-sm text-[#757675] mt-2">
              This patient will be removed from the active patient list, but their records will
              remain stored and can be restored later.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowArchiveConfirm(false)} data-testid="archive-cancel-btn"
                className="px-4 py-2 rounded-full text-xs text-[#4A4B4A] border border-[#1A1B1A]/15 hover:bg-[#FAF9F6]">
                Cancel
              </button>
              <button onClick={archivePatient} data-testid="archive-confirm-btn"
                className="px-4 py-2 rounded-full text-xs text-white bg-[#205B5A] hover:bg-[#174544]">
                Archive patient
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
